import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  QrCode, 
  User, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  Search,
  Ticket,
  RefreshCw,
  AlertTriangle,
  Camera,
  Shield,
  Phone,
  Calendar,
  MapPin,
  Loader2,
  ChevronRight,
  UserCheck,
  FileCheck
} from 'lucide-react';
import api from '@/lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

export default function QuickRegistration() {
  const [activeTab, setActiveTab] = useState('scan');
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [department, setDepartment] = useState('OPD');
  const [priority, setPriority] = useState('normal');
  const [counterId, setCounterId] = useState('OPD-1');
  
  // Patient data from ABDM
  const [scannedPatient, setScannedPatient] = useState(null);
  const [registrationResult, setRegistrationResult] = useState(null);
  
  // Verification modal
  const [showVerification, setShowVerification] = useState(false);
  const [verifying, setVerifying] = useState(false);
  
  // Token generated
  const [generatedToken, setGeneratedToken] = useState(null);
  const [showTokenModal, setShowTokenModal] = useState(false);
  
  // Pending verifications queue
  const [pendingQueue, setPendingQueue] = useState([]);
  const [todayTokens, setTodayTokens] = useState([]);
  
  // Mock patients for demo
  const [mockPatients, setMockPatients] = useState([]);

  useEffect(() => {
    loadPendingVerifications();
    loadTodayTokens();
    loadMockPatients();
  }, []);

  const loadPendingVerifications = async () => {
    try {
      const response = await api.get(`${API}/api/abdm/pending-verifications`);
      setPendingQueue(response.data.patients || []);
    } catch (error) {
      console.error('Failed to load pending verifications:', error);
    }
  };

  const loadTodayTokens = async () => {
    try {
      const response = await api.get(`${API}/api/abdm/today-tokens`);
      setTodayTokens(response.data.tokens || []);
    } catch (error) {
      console.error('Failed to load today tokens:', error);
    }
  };

  const loadMockPatients = async () => {
    try {
      const response = await api.get(`${API}/api/abdm/mock/patients`);
      setMockPatients(response.data.patients || []);
    } catch (error) {
      console.error('Failed to load mock patients:', error);
    }
  };

  const handleQuickRegistration = async () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter ABHA Address or Number');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post(`${API}/api/abdm/quick-registration`, {
        abha_identifier: searchQuery.trim(),
        department,
        counter_id: counterId,
        priority
      });

      setRegistrationResult(response.data);
      setScannedPatient(response.data.patient);
      
      if (response.data.requires_verification) {
        setShowVerification(true);
      }

      if (response.data.status === 'existing_patient') {
        toast.success('Existing patient found! Ready for check-in.');
      } else {
        toast.success('New patient profile received!');
      }

      loadPendingVerifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch ABDM profile');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyPatient = async (verified) => {
    if (!scannedPatient) return;

    setVerifying(true);
    try {
      const response = await api.post(`${API}/api/abdm/verify-patient`, {
        patient_id: scannedPatient.id,
        photo_verified: verified
      });

      if (verified && response.data.status === 'verified') {
        toast.success('Patient verified successfully!');
        setShowVerification(false);
        // Auto-generate token after verification
        await handleGenerateToken(scannedPatient.id);
      } else {
        toast.error('Verification failed. Please verify manually.');
        setShowVerification(false);
      }

      loadPendingVerifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const handleGenerateToken = async (patientId) => {
    setLoading(true);
    try {
      const response = await api.post(`${API}/api/abdm/generate-token`, {
        patient_id: patientId || scannedPatient?.id,
        department,
        priority,
        counter_id: counterId
      });

      setGeneratedToken(response.data);
      setShowTokenModal(true);
      toast.success(`Token ${response.data.token.number} generated!`);
      
      loadTodayTokens();
      
      // Reset for next patient
      setScannedPatient(null);
      setRegistrationResult(null);
      setSearchQuery('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate token');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectMockPatient = (patient) => {
    setSearchQuery(patient.healthId || patient.healthIdNumber);
  };

  const PatientCard = ({ patient, showActions = true, onVerify, onGenerateToken }) => (
    <Card className="border-l-4 border-l-purple-500">
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          {/* Patient Photo */}
          <div className="flex-shrink-0">
            {patient.profile_photo_url ? (
              <img 
                src={patient.profile_photo_url} 
                alt={patient.full_name}
                className="w-20 h-20 rounded-lg object-cover border-2 border-gray-200"
              />
            ) : (
              <div className="w-20 h-20 rounded-lg bg-purple-100 flex items-center justify-center">
                <User className="w-10 h-10 text-purple-600" />
              </div>
            )}
          </div>

          {/* Patient Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-lg truncate">{patient.full_name}</h3>
              {patient.kyc_verified && (
                <Badge className="bg-green-100 text-green-700">
                  <Shield className="w-3 h-3 mr-1" /> KYC Verified
                </Badge>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
              <div className="flex items-center gap-1">
                <Badge variant="outline" className="font-mono">
                  {patient.patient_number || 'NEW'}
                </Badge>
              </div>
              <div className="flex items-center gap-1">
                <Phone className="w-3 h-3" />
                {patient.phone || 'N/A'}
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {patient.date_of_birth || 'N/A'}
              </div>
              <div className="flex items-center gap-1">
                <User className="w-3 h-3" />
                {patient.gender || 'N/A'}
              </div>
            </div>

            {/* ABHA Info */}
            <div className="mt-2 p-2 bg-blue-50 rounded-lg">
              <div className="text-xs text-blue-700 font-medium mb-1">ABHA Details</div>
              <div className="text-sm font-mono text-blue-900">
                {patient.abha_address && <div>@ {patient.abha_address}</div>}
                {patient.abha_number && <div># {patient.abha_number}</div>}
              </div>
            </div>
          </div>

          {/* Actions */}
          {showActions && (
            <div className="flex flex-col gap-2">
              {onVerify && (
                <Button 
                  size="sm" 
                  onClick={() => onVerify(patient)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <UserCheck className="w-4 h-4 mr-1" /> Verify
                </Button>
              )}
              {onGenerateToken && (
                <Button 
                  size="sm" 
                  onClick={() => onGenerateToken(patient.id)}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <Ticket className="w-4 h-4 mr-1" /> Token
                </Button>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Quick Registration</h1>
          <p className="text-gray-500">ABDM Scan-and-Share Patient Registration</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-blue-100 text-blue-700">
            <QrCode className="w-3 h-3 mr-1" /> ABDM Ready
          </Badge>
          <Button variant="outline" size="sm" onClick={() => { loadPendingVerifications(); loadTodayTokens(); }}>
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="scan" className="flex items-center gap-1">
            <QrCode className="w-4 h-4" /> Scan & Register
          </TabsTrigger>
          <TabsTrigger value="pending" className="flex items-center gap-1">
            <Clock className="w-4 h-4" /> Pending ({pendingQueue.length})
          </TabsTrigger>
          <TabsTrigger value="tokens" className="flex items-center gap-1">
            <Ticket className="w-4 h-4" /> Today's Tokens ({todayTokens.length})
          </TabsTrigger>
          <TabsTrigger value="demo" className="flex items-center gap-1">
            <User className="w-4 h-4" /> Demo Patients
          </TabsTrigger>
        </TabsList>

        {/* Scan & Register Tab */}
        <TabsContent value="scan" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Search Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Search className="w-5 h-5 text-purple-600" />
                  Search ABHA Profile
                </CardTitle>
                <CardDescription>
                  Enter ABHA Address (e.g., ramkumar@abdm) or ABHA Number (e.g., 12-3456-7890-1234)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter ABHA Address or Number"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleQuickRegistration()}
                    data-testid="abha-search-input"
                  />
                  <Button 
                    onClick={handleQuickRegistration} 
                    disabled={loading}
                    data-testid="search-abha-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  </Button>
                </div>

                {/* Department & Priority */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Department</Label>
                    <Select value={department} onValueChange={setDepartment}>
                      <SelectTrigger data-testid="department-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="OPD">General OPD</SelectItem>
                        <SelectItem value="Emergency">Emergency</SelectItem>
                        <SelectItem value="Pediatrics">Pediatrics</SelectItem>
                        <SelectItem value="Gynecology">Gynecology</SelectItem>
                        <SelectItem value="Orthopedics">Orthopedics</SelectItem>
                        <SelectItem value="Cardiology">Cardiology</SelectItem>
                        <SelectItem value="ENT">ENT</SelectItem>
                        <SelectItem value="Ophthalmology">Ophthalmology</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Priority</Label>
                    <Select value={priority} onValueChange={setPriority}>
                      <SelectTrigger data-testid="priority-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                        <SelectItem value="emergency">Emergency</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Counter</Label>
                    <Select value={counterId} onValueChange={setCounterId}>
                      <SelectTrigger data-testid="counter-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="OPD-1">Counter 1</SelectItem>
                        <SelectItem value="OPD-2">Counter 2</SelectItem>
                        <SelectItem value="OPD-3">Counter 3</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* QR Code Display */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <QrCode className="w-5 h-5 text-purple-600" />
                  QR Code for Patient Scanning
                </CardTitle>
                <CardDescription>
                  Display this QR code for patients to scan with ABHA app
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col items-center justify-center py-8">
                <div className="w-48 h-48 bg-gray-100 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-300">
                  <div className="text-center">
                    <QrCode className="w-16 h-16 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">QR Code</p>
                    <p className="text-xs text-gray-400">(For production use)</p>
                  </div>
                </div>
                <div className="mt-4 text-center">
                  <Badge variant="outline" className="mb-2">{counterId}</Badge>
                  <p className="text-sm text-gray-500">Counter ID</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Scanned Patient Result */}
          {scannedPatient && (
            <Card className="border-2 border-purple-200 bg-purple-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileCheck className="w-5 h-5 text-purple-600" />
                  {registrationResult?.status === 'existing_patient' ? 'Existing Patient Found' : 'New Patient Profile'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <PatientCard 
                  patient={scannedPatient} 
                  showActions={true}
                  onVerify={() => setShowVerification(true)}
                  onGenerateToken={handleGenerateToken}
                />
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Pending Verifications Tab */}
        <TabsContent value="pending" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Pending Verifications</CardTitle>
              <CardDescription>
                Patients awaiting photo verification
              </CardDescription>
            </CardHeader>
            <CardContent>
              {pendingQueue.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-green-500" />
                  <p>No pending verifications</p>
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-4">
                    {pendingQueue.map((patient) => (
                      <PatientCard
                        key={patient.id}
                        patient={patient}
                        showActions={true}
                        onVerify={(p) => {
                          setScannedPatient(p);
                          setShowVerification(true);
                        }}
                        onGenerateToken={handleGenerateToken}
                      />
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Today's Tokens Tab */}
        <TabsContent value="tokens" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Today's OPD Tokens</CardTitle>
              <CardDescription>
                {new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {todayTokens.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Ticket className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                  <p>No tokens generated today</p>
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-2">
                    {todayTokens.map((token) => (
                      <div 
                        key={token.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`
                            w-16 h-16 rounded-lg flex items-center justify-center font-bold text-lg
                            ${token.status === 'waiting' ? 'bg-yellow-100 text-yellow-700' : ''}
                            ${token.status === 'in_consultation' ? 'bg-blue-100 text-blue-700' : ''}
                            ${token.status === 'completed' ? 'bg-green-100 text-green-700' : ''}
                          `}>
                            {token.token_number}
                          </div>
                          <div>
                            <p className="font-medium">{token.patient?.name}</p>
                            <p className="text-sm text-gray-500">{token.patient?.patient_number}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge className={`
                            ${token.priority === 'emergency' ? 'bg-red-100 text-red-700' : ''}
                            ${token.priority === 'urgent' ? 'bg-orange-100 text-orange-700' : ''}
                            ${token.priority === 'normal' ? 'bg-gray-100 text-gray-700' : ''}
                          `}>
                            {token.priority}
                          </Badge>
                          <p className="text-xs text-gray-500 mt-1">{token.department}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Demo Patients Tab */}
        <TabsContent value="demo" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Demo ABHA Patients</CardTitle>
              <CardDescription>
                Click on a patient to use their ABHA ID for testing
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {mockPatients.filter(p => p.status === 'ACTIVE').map((patient, index) => (
                  <div
                    key={index}
                    onClick={() => handleSelectMockPatient(patient)}
                    className="p-4 border rounded-lg cursor-pointer hover:bg-purple-50 hover:border-purple-300 transition-colors"
                    data-testid={`mock-patient-${index}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{patient.name}</p>
                        <p className="text-sm text-gray-500 font-mono">{patient.healthId}</p>
                        <p className="text-xs text-gray-400 font-mono">{patient.healthIdNumber}</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                    <div className="mt-2 flex gap-2">
                      <Badge variant="outline">{patient.gender === 'M' ? 'Male' : 'Female'}</Badge>
                      <Badge variant="outline">{patient.stateName}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Verification Modal */}
      <Dialog open={showVerification} onOpenChange={setShowVerification}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5" />
              Photo Verification
            </DialogTitle>
            <DialogDescription>
              Verify patient identity by comparing profile photo
            </DialogDescription>
          </DialogHeader>
          
          {scannedPatient && (
            <div className="space-y-4">
              <div className="flex justify-center">
                {scannedPatient.profile_photo_url ? (
                  <img 
                    src={scannedPatient.profile_photo_url}
                    alt="Patient"
                    className="w-32 h-32 rounded-lg object-cover border-4 border-gray-200"
                  />
                ) : (
                  <div className="w-32 h-32 rounded-lg bg-gray-100 flex items-center justify-center">
                    <User className="w-16 h-16 text-gray-400" />
                  </div>
                )}
              </div>
              
              <div className="text-center">
                <h3 className="font-semibold text-lg">{scannedPatient.full_name}</h3>
                <p className="text-sm text-gray-500">{scannedPatient.abha_address}</p>
                {scannedPatient.kyc_verified && (
                  <Badge className="mt-2 bg-green-100 text-green-700">
                    <Shield className="w-3 h-3 mr-1" /> KYC Verified
                  </Badge>
                )}
              </div>

              <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-yellow-800">
                    Please verify that the patient in front of you matches the profile photo shown above.
                  </p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => handleVerifyPatient(false)}
              disabled={verifying}
              className="flex-1"
            >
              <XCircle className="w-4 h-4 mr-1 text-red-500" />
              Does Not Match
            </Button>
            <Button
              onClick={() => handleVerifyPatient(true)}
              disabled={verifying}
              className="flex-1 bg-green-600 hover:bg-green-700"
              data-testid="verify-patient-btn"
            >
              {verifying ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mr-1" />
              )}
              Verified - Match
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Token Generated Modal */}
      <Dialog open={showTokenModal} onOpenChange={setShowTokenModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle2 className="w-6 h-6" />
              Token Generated Successfully!
            </DialogTitle>
          </DialogHeader>
          
          {generatedToken && (
            <div className="space-y-4">
              {/* Token Display */}
              <div className="text-center p-6 bg-gradient-to-br from-purple-500 to-purple-700 rounded-xl text-white">
                <p className="text-sm opacity-80 mb-1">Token Number</p>
                <p className="text-5xl font-bold" data-testid="generated-token-number">
                  {generatedToken.token.number}
                </p>
                <p className="text-sm opacity-80 mt-2">{generatedToken.token.department}</p>
              </div>

              {/* Patient Info */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Patient</p>
                    <p className="font-medium">{generatedToken.patient.name}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Patient ID</p>
                    <p className="font-medium">{generatedToken.patient.patient_number}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Visit Number</p>
                    <p className="font-medium">{generatedToken.visit.number}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Counter</p>
                    <p className="font-medium">{generatedToken.token.counter}</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => {
                    // Print token
                    window.print();
                  }}
                >
                  Print Token
                </Button>
                <Button 
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                  onClick={() => {
                    setShowTokenModal(false);
                    setSearchQuery('');
                  }}
                >
                  Next Patient
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
